import { Box } from '@mui/material'
import type { Editor, EditorOptions } from '@tiptap/core'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEditorContext } from 'context/EditorContext/EditorContext'
import type { ImageNodeAttributes, RichTextEditorRef } from 'mui-tiptap'
import { LinkBubbleMenu, RichTextEditor, TableBubbleMenu } from 'mui-tiptap'
import React, { FC, useCallback, useRef, useState } from 'react'
import { MentionData, OrganizationTableItem, SelectedTask } from 'types'
import { SelectedFile } from '../Comments/types'
import useExtensions from '../Comments/useExtensions'
import { User } from '../Forms/types'
import TextEditorControls from './TextEditorControls'
import TextEditorFooter from './TextEditorFooter'

// Add a local discriminator. If kind is missing, we’ll treat as "attachment".
type SelectedFileWithKind = SelectedFile & { kind?: 'inline' | 'attachment' }

type TextEditorProps = {
  organization?: OrganizationTableItem
  deal?: OrganizationTableItem
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSave: (html: string, thumbnailLink?: string) => void
  onContentChange: (html: string) => void
  newNoteContent: string
  onMentionsChange: (mentions: MentionData[]) => void
  onRemoveFile: (i: any) => void
  selectedFiles: SelectedFile[] // incoming may not have kind
  selectedTask: SelectedTask
  setSelectedFiles: React.Dispatch<React.SetStateAction<SelectedFile[]>> // we cast internally
  height: string
  isEditing?: boolean
  onCancel?: () => void
}
const getImageSize = async (file: File) => {
  try {
    const bmp = await createImageBitmap(file)
    const size = { w: bmp.width, h: bmp.height }
    bmp.close?.()
    return size
  } catch {
    return null
  }
}
const insertImageWithSize = (
  editor: Editor,
  attrs: { src: string; alt?: string; title?: string; width?: number; height?: number }
) => {
  // if your node name is "image"
  editor
    .chain()
    .focus(undefined, { scrollIntoView: false })
    .insertContent({
      type: 'image',
      attrs,
    })
    .run()
}
const TextEditor: FC<TextEditorProps> = ({
  organization,
  deal,
  onUpload,
  onSave,
  onContentChange,
  newNoteContent,
  onMentionsChange,
  onRemoveFile,
  selectedFiles,
  selectedTask,
  setSelectedFiles,
  height,
  isEditing = false,
  onCancel,
}) => {
  const { data: rawUsers } = useUserItems()
  const users = (rawUsers ?? []) as User[]

  const { editorInstance, setEditorInstance } = useEditorContext()
  const [expandedSticky, setExpandedSticky] = useState(false)
  const [isEditable, setIsEditable] = useState(true)
  const [fontSizeAnchor, setFontSizeAnchor] = React.useState<null | HTMLElement>(null)
  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']
  const [floatingOpen, setFloatingOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const rteRef = useRef<RichTextEditorRef | null>(null)
  const extensions = useExtensions({
    placeholder: 'Add comment here. type @ to tag someone',
    users: users as any[],
  })
  const isEditMode = !!isEditing
  const keepExpanded = () => setExpandedSticky(true)
  // const maybeCollapse = (e: React.FocusEvent) => {
  //   const next = e.relatedTarget as HTMLElement | null
  //   const container = (e.currentTarget as HTMLElement).closest('.rte-wrap')
  //   if (floatingOpen) return
  //   if (next && container && container.contains(next)) return
  //   setExpandedSticky(false)
  // }
  const maybeCollapse = (e: React.FocusEvent) => {
    if (isEditMode) return // ← prevent auto-collapse while editing an existing comment
    const next = e.relatedTarget as HTMLElement | null
    const container = (e.currentTarget as HTMLElement).closest('.rte-wrap')
    if (floatingOpen) return
    if (next && container && container.contains(next)) return
    setExpandedSticky(false)
  }

  const handleClear = (opts?: { collapse?: boolean }) => {
    if (editorInstance) {
      editorInstance.commands.clearContent()
      editorInstance.commands.blur()
    }
    const pm = rteRef.current?.editor?.view?.dom as HTMLElement | undefined
    pm?.blur()
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()

    // Revoke any blob URLs we created
    try {
      ;(selectedFiles as SelectedFileWithKind[]).forEach(sf => {
        if (sf?.previewUrl?.startsWith?.('blob:')) URL.revokeObjectURL(sf.previewUrl)
        if (sf?.url?.startsWith?.('blob:')) URL.revokeObjectURL(sf.url)
      })
    } catch {
      // intentionally empty
    }

    setSelectedFiles([])
    if (opts?.collapse !== false) setExpandedSticky(false)
  }

  // ---- mark pasted/dropped images as inline ----
  const buildImageAttrsFromFiles = useCallback(
    async (files: File[]): Promise<ImageNodeAttributes[]> => {
      const temps: SelectedFileWithKind[] = files.map(f => {
        const id = `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2)}`
        const url = URL.createObjectURL(f)
        return { id, url, name: f.name, mimeType: f.type, file: f, kind: 'inline' }
      })

      setSelectedFiles(prev => [...(prev as SelectedFileWithKind[]), ...temps])
      return temps.map(t => ({ src: t.url!, alt: t.name }))
    },
    [setSelectedFiles]
  )

  const THUMB_MAX_PX = 240 // hard cap
  const THUMB_FRACTION = 0.33 // 33% of editor width (optional)
  const THUMB_MIN_PX = 160

  const handleNewImageFiles = useCallback(
    async (files: File[], insertPosition?: number) => {
      const editor = rteRef.current?.editor
      if (!editor || files.length === 0) return

      const editorEl = wrapRef.current?.querySelector('.rte-editor') as HTMLElement | null
      const containerWidth = editorEl?.clientWidth ?? 720

      const computed = Math.floor(containerWidth * THUMB_FRACTION)
      const targetW = Math.max(THUMB_MIN_PX, Math.min(THUMB_MAX_PX, computed))

      const temps: SelectedFileWithKind[] = []

      // If a position is provided (drop), set selection there once
      if (typeof insertPosition === 'number') {
        editor.commands.setTextSelection(insertPosition)
      }

      for (const f of files) {
        const url = URL.createObjectURL(f)

        temps.push({
          id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2)}`,
          url,
          name: f.name,
          mimeType: f.type,
          file: f,
          kind: 'inline',
        })

        const sz = await getImageSize(f)
        const targetH = sz ? Math.round((targetW * sz.h) / sz.w) : undefined

        // ✅ insert the node yourself so width/height can exist
        insertImageWithSize(editor, {
          src: url,
          alt: f.name,
          width: targetW,
          ...(targetH ? { height: targetH } : {}),
        })

        // optional: add a paragraph after each image so typing continues nicely
        editor.chain().insertContent({ type: 'paragraph' }).run()
      }

      // keep typing flow
      editor.commands.focus(undefined, { scrollIntoView: false })

      setSelectedFiles(prev => [...(prev as SelectedFileWithKind[]), ...temps])
    },
    [setSelectedFiles]
  )

  // drop/paste handlers unchanged (call handleNewImageFiles)
  const pasteDropGuard = useRef<{ ts: number } | null>(null)
  const ranRecently = (ms = 100) => pasteDropGuard.current && Date.now() - pasteDropGuard.current.ts < ms
  const markNow = () => (pasteDropGuard.current = { ts: Date.now() })

  const handleDrop: NonNullable<EditorOptions['editorProps']['handleDrop']> = useCallback(
    (view, event) => {
      if (!(event instanceof DragEvent) || !event.dataTransfer) return false
      const imageFiles = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/'))
      if (imageFiles.length === 0) return false
      event.preventDefault()
      event.stopImmediatePropagation?.()
      event.stopPropagation?.()
      if (ranRecently()) return true
      markNow()
      const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
      handleNewImageFiles(imageFiles, pos)
      return true
    },
    [handleNewImageFiles]
  )

  const handlePaste: NonNullable<EditorOptions['editorProps']['handlePaste']> = useCallback(
    (_view, event) => {
      const dt = event.clipboardData
      if (!dt) return false
      let images = Array.from(dt.files ?? []).filter(f => f.type.startsWith('image/'))
      if (images.length === 0) {
        images = Array.from(dt.items ?? [])
          .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
          .map(it => it.getAsFile())
          .filter((f): f is File => !!f)
      }
      if (images.length === 0) return false
      event.preventDefault()
      event.stopImmediatePropagation?.()
      event.stopPropagation?.()
      if (ranRecently()) return true
      markNow()
      handleNewImageFiles(images)
      return true
    },
    [handleNewImageFiles]
  )

  const openFontMenu = (el: HTMLElement) => {
    keepExpanded()
    setFontSizeAnchor(el)
    setFloatingOpen(true)
  }

  // ---- sync only inline images with document HTML ----
  const syncAttachmentsWithDoc = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const currentSrcs = new Set(
      Array.from(doc.querySelectorAll('img'))
        .map(img => img.getAttribute('src') || '')
        .filter(Boolean)
    )

    setSelectedFiles(prevRaw => {
      const prev = prevRaw as SelectedFileWithKind[]

      const inline: SelectedFileWithKind[] = []
      const attachments: SelectedFileWithKind[] = []

      for (const sf of prev) {
        // default to 'attachment' when kind is missing
        if (sf.kind === 'inline') inline.push(sf)
        else attachments.push(sf)
      }

      const nextInline = inline.filter(sf => {
        const src = sf.url || sf.previewUrl || ''
        return src && currentSrcs.has(src)
      })

      // revoke blobs for removed inline images
      for (const sf of inline) {
        const src = sf.url || sf.previewUrl || ''
        const stillThere = nextInline.some(n => (n.url || n.previewUrl) === src)
        if (!stillThere && src?.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(src)
          } catch {
            // intentionally empty
          }
        }
      }

      return [...nextInline, ...attachments]
    })
  }

  // tiny rAF debounce so we don’t parse HTML on every keystroke
  const rafId = useRef<number | null>(null)
  const syncWithRaf = (html: string) => {
    if (rafId.current) cancelAnimationFrame(rafId.current)
    rafId.current = requestAnimationFrame(() => {
      syncAttachmentsWithDoc(html)
    })
  }

  const createInstance = React.useCallback(
    ({ editor }: { editor: Editor }) => {
      setEditorInstance(editor)
      if (isEditMode) {
        keepExpanded()
        setFloatingOpen(false)
        editor.commands.focus(undefined, { scrollIntoView: false })
      }
    },
    [isEditMode, setEditorInstance, keepExpanded]
  )
  React.useEffect(() => {
    if (isEditMode && rteRef.current?.editor) {
      keepExpanded()
      setFloatingOpen(false)
      // focus without scrolling the page
      rteRef.current.editor.commands.focus(undefined, { scrollIntoView: false })
    }
  }, [isEditMode, rteRef.current?.editor])
  return (
    <Box
      ref={wrapRef}
      tabIndex={-1}
      className={`rte-wrap ${expandedSticky ? 'expanded' : ''} ${floatingOpen ? 'menu-open' : ''}`}
      onFocusCapture={keepExpanded}
      onBlurCapture={maybeCollapse}
      sx={{
        // '--rte-collapsed': '56px',
        // '--rte-expanded': height || '28vh',
        // '--rte-max': height || '50vh',
        position: 'relative',
        '& .rte-activator': {
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          display: 'block',
          cursor: 'text',
          background: 'transparent',
        },
        '&:focus-within .rte-activator, &.menu-open .rte-activator': { display: 'none' },
        '& .rte-activator::after': { position: 'absolute', left: 16, top: 10, opacity: 0.5, pointerEvents: 'none' },
        '&:focus-within .rte-toolbar, &.expanded .rte-toolbar, &.menu-open .rte-toolbar': { display: 'flex' },
        '& .rte-toolbar': {
          position: 'sticky',
          top: 0,
          zIndex: 3,
          display: 'none',
          background: 'transparent !important',
          px: 1,
          py: 0.5,
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'background.help',
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            pointerEvents: 'none',
            zIndex: 0,
          },
          '& > *': { position: 'relative', zIndex: 1, background: 'transparent' },
        },
        '& .rte-editor': {
          minHeight: 'var(--rte-collapsed)',
          maxHeight: 'var(--rte-max)',
          transition: 'min-height 200ms ease, padding 200ms ease, box-shadow 200ms ease',
          scrollbarGutter: 'stable',
        },
        '&:focus-within .rte-editor, &.menu-open .rte-editor': {
          minHeight: 'var(--rte-expanded)',
          boxShadow: theme => `0 0 0 1px ${theme.palette.divider} inset`,
          '&:focus': { border: '1px solid', borderColor: 'text.success' },
        },
        '& .rte-footer': { display: 'none' },
        '&:focus-within .rte-footer, &.expanded .rte-footer, &.menu-open .rte-footer': { display: 'flex' },
      }}
    >
      <div
        className="rte-activator"
        onMouseDown={e => {
          e.preventDefault()
          e.stopPropagation()
          rteRef.current?.editor?.commands.focus(undefined, { scrollIntoView: false })
          keepExpanded()
        }}
      />
      <RichTextEditor
        ref={rteRef}
        extensions={extensions}
        content={(newNoteContent ?? '').trim() || ''}
        editable={isEditable}
        editorProps={{
          handleDrop,
          handlePaste,
          transformPastedHTML: html => html.replace(/<img[\s\S]*?>/gi, ''),
          handleClickOn: () => false,
          handleScrollToSelection: () => false,
          attributes: { class: 'rte-editor', style: 'overflow-y:auto; text-align:left; outline:0;' },
        }}
        // onCreate={({ editor }) => setEditorInstance(editor)}
        onCreate={createInstance}
        renderControls={() => (
          <TextEditorControls
            editor={editorInstance}
            fontSizes={fontSizes}
            fontSizeAnchor={fontSizeAnchor}
            openFontMenu={openFontMenu}
            setFontSizeAnchor={setFontSizeAnchor}
            onUploadFiles={buildImageAttrsFromFiles}
            forceExpand={keepExpanded}
            setFloatingOpen={setFloatingOpen}
            menuContainer={wrapRef.current}
          />
        )}
        onUpdate={() => {
          const html = rteRef.current?.editor?.getHTML() || ''
          onContentChange(html)
          // keep inline images in sync with doc; attachments are left alone
          syncWithRaf(html)

          // mentions
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const els = doc.querySelectorAll("[data-type='mention']")
          const mentions: MentionData[] = Array.from(els).map(el => ({
            id: el.getAttribute('data-id') || '',
            name: el.getAttribute('data-label') || '',
            imageUrl: el.getAttribute('data-image-url') || '',
          }))
          onMentionsChange(mentions)
        }}
        RichTextFieldProps={{
          className: 'rte-field',
          MenuBarProps: {
            className: 'rte-toolbar',
            hide: false,
            sx: { m: 0, '& .MuiIconButton-root': { p: 0.5, m: 0 } },
          },
          footer: (
            <TextEditorFooter
              isEditing={isEditMode}
              keepExpanded={keepExpanded}
              onSave={onSave}
              onUpload={onUpload} // files added here are "attachments" (not pruned)
              onCancel={onCancel}
              onRemoveFile={onRemoveFile}
              onClear={() => handleClear()}
              editorInstance={editorInstance}
              rteRef={rteRef}
              selectedFiles={selectedFiles}
              selectedData={selectedTask ?? deal ?? organization}
            />
          ),
          onMouseDownCapture: (e: React.MouseEvent) => {
            const el = e.target as HTMLElement
            if (el.closest('button,[role="button"],[aria-haspopup]')) {
              e.preventDefault()
              e.stopPropagation()
              setFloatingOpen(true)
              rteRef.current?.editor?.commands.focus(undefined, { scrollIntoView: false })
              keepExpanded()
            }
          },
          onMouseUpCapture: () => {
            setTimeout(() => {
              const anyPopover = !!document.querySelector(
                '.MuiPopover-root, .MuiMenu-root, [role="presentation"][data-mui-popper]'
              )
              if (!anyPopover) setFloatingOpen(false)
            }, 50)
          },
        }}
      >
        {() => (
          <>
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </>
        )}
      </RichTextEditor>
    </Box>
  )
}

export default TextEditor
